import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, AlertTriangle, Clock, ArrowRight, X } from '@/components/icons';
import { useBillingStatus } from '@/hooks/use-billing';
import { useAuth } from '@/contexts/auth-context';
import { useProperty } from '@/contexts/property-context';
import { isActingResident } from '@/lib/user-roles';
import { PlanUpgradeDrawer } from '@/components/billing/PlanUpgradeDrawer';

const DISMISS_KEY_PREFIX = 'koinon_dismissed_sub_banner_';

export const TrialBanner: React.FC = () => {
  const { t } = useTranslation();
  const { user, appMode } = useAuth();
  const { residentMode, isResidentScope } = useProperty();

  const canManage = Boolean(
    user?.role === 'superadmin' ||
    user?.role === 'manager'
  );
  const isResident = isActingResident(user, { appMode, residentMode, isResidentScope });
  const isManager = canManage && !isResident;

  const { data: billing } = useBillingStatus(undefined, { enabled: isManager });
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const isTrialing = billing?.subscription_status === 'trialing';
  const isCanceling =
    billing?.subscription_status === 'active' && billing.cancel_at_period_end;
  const isCanceled = billing?.subscription_status === 'canceled';
  const isUnpaid = billing?.subscription_status === 'unpaid';
  const isReadOnly = !billing?.is_writable || isCanceled || isUnpaid;

  useEffect(() => {
    if (!isManager) return;
    if (isCanceling) {
      try {
        const key = `${DISMISS_KEY_PREFIX}canceling_${billing?.current_period_end || 'active'}`;
        setIsDismissed(localStorage.getItem(key) === 'true');
      } catch {
        setIsDismissed(false);
      }
    } else if (isTrialing) {
      try {
        const key = `${DISMISS_KEY_PREFIX}trial_${billing?.trial_ends_at || 'active'}`;
        setIsDismissed(localStorage.getItem(key) === 'true');
      } catch {
        setIsDismissed(false);
      }
    } else {
      setIsDismissed(false);
    }
  }, [isManager, isCanceling, isTrialing, billing?.current_period_end, billing?.trial_ends_at]);

  if (!isManager || !billing) return null;

  if ((!isTrialing && !isCanceling && !isReadOnly) || ((isCanceling || isTrialing) && isDismissed)) {
    return null;
  }

  const formatDate = (iso: string | null) => {
    if (!iso) return t('trial_banner.period_end_fallback', 'the end of your billing period');
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const readOnlyMessage = isCanceled
    ? t(
      'trial_banner.readonly_canceled',
      'Your subscription has been canceled. This workspace is in read-only mode.'
    )
    : isUnpaid
      ? t(
        'trial_banner.readonly_unpaid',
        'Your subscription payment has failed. This workspace is in read-only mode.'
      )
      : t(
        'trial_banner.readonly_ended',
        'Your trial has ended. This workspace is in read-only mode.'
      );

  const readOnlyCta =
    isCanceled || isUnpaid
      ? t('trial_banner.cta_reactivate', 'Reactivate')
      : t('trial_banner.cta_activate', 'Activate Subscription');

  const handleDismiss = () => {
    try {
      if (isCanceling) {
        const key = `${DISMISS_KEY_PREFIX}canceling_${billing?.current_period_end || 'active'}`;
        localStorage.setItem(key, 'true');
      } else if (isTrialing) {
        const key = `${DISMISS_KEY_PREFIX}trial_${billing?.trial_ends_at || 'active'}`;
        localStorage.setItem(key, 'true');
      }
    } catch {
      // Ignore storage errors
    }
    setIsDismissed(true);
  };

  return (
    <>
      <div
        className={`w-full px-4 py-2 flex items-center justify-between text-xs transition-colors border-b ${
          isCanceling
            ? 'bg-amber-500/10 border-amber-500/20 text-amber-700 dark:text-amber-400'
            : isReadOnly
              ? 'bg-destructive/10 border-destructive/20 text-destructive'
              : 'bg-primary/10 border-primary/20 text-foreground'
        }`}
      >
        <div className="flex items-center gap-2">
          {isCanceling ? (
            <Clock className="w-4 h-4 text-amber-500 shrink-0" />
          ) : isReadOnly ? (
            <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
          ) : (
            <Sparkles className="w-4 h-4 text-primary shrink-0" />
          )}
          <span className="font-medium">
            {isTrialing
              ? t('trial_banner.trial_remaining', '{{count}} days remaining in your {{plan}} trial.', {
                count: billing.days_remaining ?? 30,
                plan: billing.plan_name.replace(' (Trial)', ''),
              })
              : isCanceling
                ? t('trial_banner.ends_on', 'Your subscription ends on {{date}}.', {
                  date: formatDate(billing.current_period_end),
                })
                : readOnlyMessage}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setIsUpgradeOpen(true)}
            className={`font-semibold flex items-center gap-1 transition-colors hover:underline cursor-pointer ${
              isCanceling
                ? 'text-amber-600 dark:text-amber-400 font-bold'
                : isReadOnly
                  ? 'text-destructive font-bold'
                  : 'text-primary'
            }`}
          >
            <span>
              {isTrialing
                ? t('trial_banner.cta_subscribe', 'Subscribe Now')
                : isCanceling
                  ? t('trial_banner.cta_reactivate', 'Reactivate')
                  : readOnlyCta}
            </span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>

          {(isCanceling || isTrialing) && (
            <button
              type="button"
              onClick={handleDismiss}
              className={`p-1 rounded-md transition-colors cursor-pointer ${
                isCanceling
                  ? 'hover:bg-amber-500/20 text-amber-700 dark:text-amber-400'
                  : 'hover:bg-primary/20 text-foreground/70 hover:text-foreground'
              }`}
              title={t('trial_banner.dismiss', 'Dismiss')}
              aria-label={t('trial_banner.dismiss', 'Dismiss')}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <PlanUpgradeDrawer
        isOpen={isUpgradeOpen}
        onClose={() => setIsUpgradeOpen(false)}
      />
    </>
  );
};
